import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid
} from '@mui/material';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import DraggableGroceryItem from './DraggableGroceryItem';
import DropZone from './DropZone';

const GroceryDragLayout = ({ 
    items, 
    loading, 
    children, // Header content (e.g. VoiceRecorder)
    footer,   // Footer content (e.g. Load More)
    dropZones = [], // Array of { id, label, color, hoverColor }
    onDragDrop, // (itemId, dropZoneId) => void
    emptyState, // React node for empty state
    currentUserId,
    deleteConfirmationText = "Delete Item?",
}) => {
    const [activeItem, setActiveItem] = useState(null);
    const [itemConfirmingAction, setItemConfirmingAction] = useState(null); // Item waiting for confirmation
    const [pendingActionZone, setPendingActionZone] = useState(null); // Zone that triggered confirmation (if any)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Configure sensors for touch and pointer
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );

    const handleDragStart = (event) => {
        const item = items.find(i => i.id === event.active.id);
        setActiveItem(item);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveItem(null);

        if (!over) return;

        const item = items.find(i => i.id === active.id);
        if (!item) return;

        // Check if the drop zone requires confirmation (e.g. trash)
        // For now, hardcode 'trash' as requiring confirmation, or pass it in config
        if (over.id === 'trash') {
            setItemConfirmingAction(item);
            setPendingActionZone(over.id);
            setShowConfirmDialog(true);
        } else {
            // Execute immediately for other zones
            onDragDrop(item.id, over.id);
        }
    };

    const handleConfirmAction = () => {
        if (itemConfirmingAction && pendingActionZone) {
            onDragDrop(itemConfirmingAction.id, pendingActionZone);
            closeDialog();
        }
    };

    const closeDialog = () => {
        setItemConfirmingAction(null);
        setPendingActionZone(null);
        setShowConfirmDialog(false);
    };

    if (loading && items.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" color="#718096">
                    Loading...
                </Typography>
            </Box>
        );
    }

    return (
        <DndContext 
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <Grid container spacing={3} sx={{ height: '100%' }}>
                {/* Left Content - Grocery List */}
                <Grid item xs={12} md={9}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        
                        {/* Header Content */}
                        {children}

                        {/* List Items */}
                        {items.length === 0 ? (
                            emptyState
                        ) : (
                            <SortableContext
                                items={items.map(item => item.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {items.map(item => (
                                        <DraggableGroceryItem
                                            key={item.id}
                                            item={item}
                                            currentUserId={currentUserId}
                                        />
                                    ))}
                                </Box>
                            </SortableContext>
                        )}
                        
                        {/* Footer Content */}
                        {footer}
                    </Box>
                </Grid>

                {/* Right Sidebar - Drop Zones */}
                <Grid item xs={12} md={3}>
                    <Stack spacing={3} sx={{ position: 'sticky', top: 20 }}>
                        <Stack spacing={2}>
                            {dropZones.map(zone => (
                                <DropZone
                                    key={zone.id}
                                    id={zone.id}
                                    label={zone.label}
                                    color={zone.color}
                                    hoverColor={zone.hoverColor}
                                    icon={zone.icon}
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            {/* Drag Overlay */}
            <DragOverlay>
                {activeItem ? (
                    <Box
                        sx={{
                            p: 3,
                            bgcolor: '#ffffff',
                            border: '2px solid #3182ce',
                            borderRadius: 3,
                            boxShadow: 8,
                            minHeight: 100,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748' }}>
                            {activeItem.name}
                        </Typography>
                    </Box>
                ) : null}
            </DragOverlay>

            {/* Confirmation Dialog */}
            <Dialog 
                open={showConfirmDialog} 
                onClose={closeDialog}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                    {deleteConfirmationText}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                        Are you sure you want to delete <strong>{itemConfirmingAction?.name}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button 
                        onClick={closeDialog}
                        variant="outlined"
                        size="large"
                        sx={{ 
                            minWidth: 120,
                            height: 56,
                            fontSize: '1.1rem'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmAction}
                        color="error"
                        variant="contained"
                        size="large"
                        sx={{ 
                            minWidth: 120,
                            height: 56,
                            fontSize: '1.1rem'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </DndContext>
    );
};

export default GroceryDragLayout;
